/** @file chordResolver: finds the effective chord (overrides applied) of a binding id across the key tables. */
import { applyOverrides } from '@zilo/pose3d-shortcuts/applyOverrides';
import type { Binding, Platform } from '@zilo/pose3d-shortcuts/types';

/** Ids that moved into the global transport table; a menu that still asks for the old id gets the new binding's chord. */
const MOVED: Readonly<Record<string, string>> = {
  'viewport.key.joint': 'transport.keyJoint', 'timeline.keyPose': 'transport.keyJoint',
  'viewport.key.all': 'transport.keyAll', 'timeline.keyAll': 'transport.keyAll',
  'viewport.play': 'transport.play', 'timeline.playPause': 'transport.play',
  'viewport.frame.prev': 'transport.prevFrame', 'timeline.prevFrame': 'transport.prevFrame',
  'viewport.frame.next': 'transport.nextFrame', 'timeline.nextFrame': 'transport.nextFrame',
  'viewport.tool.hand': 'tool.hand', 'viewport.tool.move': 'tool.move', 'viewport.tool.rotate': 'tool.rotate',
  'viewport.tool.scale': 'tool.scale', 'viewport.tool.ik': 'tool.ik',
  'viewport.space.toggle': 'gizmo.space', 'viewport.pivot.toggle': 'gizmo.pivot',
};

/** The chord of the first id any table binds (a moved id counts as its new one), or undefined (a menu item then shows no chord). */
export function chordResolver(tables: Binding[][], overrides: Binding[], platform: Platform): (...ids: string[]) => string | undefined {
  const effective = tables.flatMap((t) => applyOverrides(t, overrides, platform).table);
  return (...ids) => {
    for (const id of ids.flatMap((i) => [i, MOVED[i]])) {
      const found = id === undefined ? undefined : effective.find((b) => b.id === id);
      if (found !== undefined) return found.keys;
    }
    return undefined;
  };
}
